/**
 * Azure endpoint configuration.
 *
 * In E2E mode (VITE_E2E_MODE=true), both ARM and Otto control plane endpoints
 * default to the mock control plane. The URL is configured via VITE_ARM_BASE_URL
 * (set by dev scripts with correct port offsets).
 *
 * Override priority (highest wins):
 *   1. VITE_ARM_ENDPOINT / VITE_LOGIC_ENDPOINT (explicit env var)
 *   2. window.__OTTO__ARM_ENDPOINT / window.__OTTO__LOGIC_ENDPOINT (runtime injection)
 *   3. VITE_ARM_BASE_URL (set by dev scripts, supports --port offsets)
 *   4. E2E fallback (http://localhost:4400)
 *   5. Production default (brazilus endpoints)
 */

import { getArmBaseUrl } from './armConfig';
import { getOttoEnv } from './envContext';

const isE2EMode = typeof import.meta !== 'undefined' && import.meta.env
  ? import.meta.env.VITE_E2E_MODE === 'true'
  : false;

/** Azure Resource Manager endpoint */
const defaultArmEndpoint = 'https://management.azure.com';
const armEndpointEnv = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env.VITE_ARM_ENDPOINT : undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const armEndpointRuntime = typeof window !== 'undefined' ? (window as any).__OTTO__ARM_ENDPOINT : undefined;
// In E2E mode, getArmBaseUrl() returns the mock control plane URL (respects --port offsets).
export const armEndpoint = armEndpointEnv || armEndpointRuntime || (isE2EMode ? getArmBaseUrl() : defaultArmEndpoint);

/** Logic Apps automation endpoint (Otto control plane) */
const logicEndpointEnv = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env.VITE_LOGIC_ENDPOINT : undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const logicEndpointRuntime = typeof window !== 'undefined' ? (window as any).__OTTO__LOGIC_ENDPOINT : undefined;

/**
 * Default Otto control-plane region. Used as a safe fallback whenever a
 * location value is missing or fails validation, so the bearer token is never
 * sent to an arbitrary/attacker-controlled host. See `sanitizeLocation`.
 */
const DEFAULT_LOCATION = 'brazilus';

/**
 * Azure region canonical names are lowercase alphanumeric (e.g. `eastus`,
 * `westus2`, `brazilus`). After normalization, display names collapse to the
 * same shape (`West US 2` -> `westus2`) and stage regions become `westus2stage`
 * (parens are stripped). Anything outside this set — `.`, `#`, `/`, `@`, `:`,
 * `%`, etc. — is rejected to prevent hostname injection (MSRC-122609). The
 * length bound keeps the value within a single DNS label and rejects absurd
 * input early.
 */
const VALID_LOCATION_PATTERN = /^[a-z0-9]{1,48}$/;

/**
 * Normalize and strictly validate a location value before it is used as a
 * component of an API hostname. Returns the safe normalized region, or `null`
 * if the input is not a well-formed Azure region (i.e. potentially malicious).
 * Defensively fails closed on any non-string input despite the TS signature.
 */
const sanitizeLocation = (location: unknown): string | null => {
  if (typeof location !== 'string') {
    return null;
  }
  const normalized = location.replace(/\s+/g, '').replace(/[()]/g, '').toLowerCase();
  return VALID_LOCATION_PATTERN.test(normalized) ? normalized : null;
};

/** Rejected-location values already reported, to avoid console/telemetry flood under attack. */
const reportedRejections = new Set<string>();
/** Cap the dedupe set so a flood of distinct invalid values can't grow it unbounded (memory DoS). */
const MAX_TRACKED_REJECTIONS = 100;
/**
 * Cap the length of each retained dedupe key. A crafted URL can supply a very
 * large path segment; truncating before we store it keeps a single rejection
 * from pinning a large attacker-controlled string in memory. The true length is
 * still reported to telemetry separately (see below).
 */
const MAX_TRACKED_KEY_LENGTH = 64;

/**
 * Report a rejected (invalid/malicious) location value. Deduped per distinct
 * value, and capped so a sustained attack with many distinct values can neither
 * flood the console/telemetry pipeline nor grow the dedupe set without bound.
 * The stored dedupe key is also length-capped so an oversized path segment
 * can't be retained verbatim. Fire-and-forget dynamic import keeps
 * `endpoints.ts` free of static telemetry/store dependencies and avoids any
 * module-init ordering issues; failures here must never break callers.
 */
const reportRejectedLocation = (rawLocation: unknown): void => {
  const raw = typeof rawLocation === 'string' ? rawLocation : `<${typeof rawLocation}>`;
  // Capture the real length for telemetry before truncating the retained key.
  const originalLength = raw.length;
  // Bound the retained key so one huge path segment can't pin large memory.
  const key = raw.length > MAX_TRACKED_KEY_LENGTH ? raw.slice(0, MAX_TRACKED_KEY_LENGTH) : raw;
  if (reportedRejections.has(key) || reportedRejections.size >= MAX_TRACKED_REJECTIONS) {
    // Already reported, or we've signaled enough — bound memory and rate-limit.
    return;
  }
  reportedRejections.add(key);
  console.warn('[endpoints] Rejected invalid location value; falling back to default region.');
  void import('./telemetryService')
    .then(({ telemetryService }) => {
      // Numeric value belongs in measurements (3rd arg), never the raw value.
      telemetryService.trackEvent('security.location.rejected', undefined, { length: originalLength });
    })
    .catch(() => {
      /* telemetry is best-effort; never throw from the security fallback path */
    });
};

/** Private logic endpoint - only accessible via getLogicEndpoint() */
let _logicEndpoint = logicEndpointEnv || logicEndpointRuntime;

/**
 * Set the Logic Apps automation endpoint based on location.
 * Call this when the region changes (from context chip or URL path).
 * @param location - Azure region name (e.g., 'eastus', 'westeurope')
 */
export const setLogicEndpoint = (location?: string | null): void => {
  // If a custom endpoint is configured via env or runtime, don't override
  if (logicEndpointEnv || logicEndpointRuntime) {
    return;
  }
  // Build location-specific endpoint (validated). Missing/invalid -> safe default.
  _logicEndpoint = getRegionalEndpoint(location ?? DEFAULT_LOCATION);
};

export const getRegionalEndpoint = (location: string): string => {
  const normalizedLocation = sanitizeLocation(location);
  if (!normalizedLocation) {
    // Invalid/malicious location: never build an arbitrary host with the user's
    // bearer token attached. Fall back to the known-good default region.
    reportRejectedLocation(location);
    return `https://auto.${DEFAULT_LOCATION}.logic.azure.com`;
  }
  return `https://auto.${normalizedLocation}.logic.azure.com`;
};

/**
 * Get the current Logic Apps automation endpoint.
 * @returns The Logic endpoint URL for the current location
 */
export const getLogicEndpoint = (): string => {
  return _logicEndpoint;
};

/** Template storage (Azure Front Door) endpoints, mirrored from Azure/ProjectAutoTemplates. */
const TEMPLATES_ENDPOINT_PROD = 'https://autotemplates-csfef2cyfrhueae4.b02.azurefd.net';
const TEMPLATES_ENDPOINT_CANARY = 'https://ottocanarytemplates-dgdth8hnbad6gkc7.b02.azurefd.net';

/**
 * Get the template storage endpoint for the current environment.
 *
 * Evaluated per call (matching `getOttoEnv()` semantics). Priority:
 *   1. VITE_TEMPLATES_ENDPOINT (explicit env var — dev proxy / local override)
 *   2. window.__OTTO__TEMPLATES_ENDPOINT (runtime injection)
 *   3. Environment mapping: 'prod' and 'staging' (staging is the first step of
 *      prod) → prod storage; 'canary' and undefined (local dev) → canary storage.
 */
export const getTemplatesEndpoint = (): string => {
  const envOverride = typeof import.meta !== 'undefined' && import.meta.env
    ? import.meta.env.VITE_TEMPLATES_ENDPOINT
    : undefined;
  if (envOverride) return envOverride;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const runtimeOverride = typeof window !== 'undefined' ? (window as any).__OTTO__TEMPLATES_ENDPOINT : undefined;
  if (runtimeOverride) return runtimeOverride;

  const env = getOttoEnv();
  return env === 'prod' || env === 'staging' ? TEMPLATES_ENDPOINT_PROD : TEMPLATES_ENDPOINT_CANARY;
};

export const apiVersions = {
    otto: '2026-04-01-preview',
    /**
     * Automation project *application* environment settings API.
     * Backend (BPM PR #15323206) registers routes with
     * `FlowConstants.Preview20240601ApiVersion` and accepts any api-version
     * >= the introduced version, so newer versions also work.
     * Kept as a separate constant so env-settings calls can evolve
     * independently of other Otto routes.
     */
    environmentSettings: '2026-04-01-preview',
};
